import { PayloadAction } from '@reduxjs/toolkit';

import { VariantsState } from 'editor/src/store/variants/types';

export interface ResolvePlaceholderUidPayload {
  oldProductUid: string;
  newProductUid: string;
}

const PLACEHOLDER_UID_PREFIX = 'product_uid_placeholder__';

/**
 * Validates that a product UID has a safe format.
 *
 * Two shapes arrive here:
 *   - real catalogue UIDs: alphanumerics, underscores, hyphens, colons
 *     (e.g. `de_gcsb_flat_product_4-0_hor_130x180-mm_170-gsm-coated-silk_no`)
 *     — kept on a strict allowlist, their alphabet is closed;
 *   - storefronts placeholder UIDs: `product_uid_placeholder__` followed by
 *     `key::value__key::value…` segments whose values are i18n-labelled
 *     attribute options. That alphabet is OPEN — `4+4`, `Brillant & Mat`,
 *     `Größe`, `L'Original`, decimals, parentheses. An allowlist there can
 *     only ever be one unshipped character away from silently swallowing the
 *     placeholder→real swap, and when the swap is dropped the variant keeps
 *     its placeholder UID, the design template (stored under the real UID)
 *     never matches it, and the editor re-requests templates forever without
 *     re-emitting its variation groups (GAPI-47583; previously GPS-25623 for
 *     spaces and dots).
 *
 * Rejecting a placeholder does not sanitise anything: it arrives in
 * `productVariations` before this reducer ever runs and is echoed back to the
 * host in `requestDesignTemplates` either way. The only thing rejection
 * changes is whether the rename happens. So the check is inverted for them —
 * it rejects only what no attribute label can legitimately contain (control
 * characters and angle brackets) and accepts every label character, rather
 * than enumerating the ones seen so far and dropping the rest.
 *
 * Length is still hard-capped at 500 to prevent pathological inputs.
 */
const isValidProductUid = (uid: string): boolean => {
  if (!uid || typeof uid !== 'string' || uid.length > 500) {
    return false;
  }
  if (uid.startsWith(PLACEHOLDER_UID_PREFIX)) {
    // eslint-disable-next-line no-control-regex
    return !/[<>\u0000-\u001f]/.test(uid);
  }
  const PRODUCT_UID_PATTERN = /^[a-zA-Z0-9_:\-. (),/]+$/;
  return PRODUCT_UID_PATTERN.test(uid);
};

/**
 * This reducer performs a surgical update of a placeholder product UID to the real product UID.
 * It updates all relevant parts of the variants state without resetting the entire product tab.
 */
const resolvePlaceholderUidReducer = (state: VariantsState, action: PayloadAction<ResolvePlaceholderUidPayload>) => {
  const { oldProductUid, newProductUid } = action.payload;

  // Validate inputs - early return if UIDs are missing or invalid
  if (!isValidProductUid(oldProductUid) || !isValidProductUid(newProductUid)) {
    return;
  }

  // 1. Update productVariations
  const variationIndex = state.product.productVariations.findIndex((v) => v.productUid === oldProductUid);
  if (variationIndex !== -1) {
    state.product.productVariations[variationIndex].productUid = newProductUid;
  }

  // 2. Update variationGroups - find any variation info that has the placeholder UID
  state.variationGroups.forEach((group) => {
    group.variationsInfo.forEach((info) => {
      if (info.variation.productUid === oldProductUid) {
        info.variation.productUid = newProductUid;
      }
    });
  });

  // 3. Update outOfStock if it contains the placeholder
  if (state.product.outOfStock[oldProductUid] !== undefined) {
    state.product.outOfStock[newProductUid] = state.product.outOfStock[oldProductUid];
    delete state.product.outOfStock[oldProductUid];
  }

  // 4. Update unavailableProducts if it contains the placeholder
  if (state.product.unavailableProducts[oldProductUid] !== undefined) {
    state.product.unavailableProducts[newProductUid] = state.product.unavailableProducts[oldProductUid];
    delete state.product.unavailableProducts[oldProductUid];
  }

  // 5. Update selectedProductUid if it was the placeholder
  if (state.selectedProductUid === oldProductUid) {
    state.selectedProductUid = newProductUid;
  }

  // 6. Update layoutPerProductUids if it contains the placeholder
  if (state.layoutPerProductUids[oldProductUid] !== undefined) {
    state.layoutPerProductUids[newProductUid] = state.layoutPerProductUids[oldProductUid];
    delete state.layoutPerProductUids[oldProductUid];
  }

  // 7. Update groupedSpreadsPerProductUids if it contains the placeholder
  if (state.groupedSpreadsPerProductUids[oldProductUid] !== undefined) {
    state.groupedSpreadsPerProductUids[newProductUid] = state.groupedSpreadsPerProductUids[oldProductUid];
    delete state.groupedSpreadsPerProductUids[oldProductUid];
  }

  // 8. Update resizableElementPerProductUids if it contains the placeholder
  if (state.resizableElementPerProductUids?.[oldProductUid] !== undefined) {
    state.resizableElementPerProductUids[newProductUid] = state.resizableElementPerProductUids[oldProductUid];
    delete state.resizableElementPerProductUids[oldProductUid];
  }
};

export default resolvePlaceholderUidReducer;
